/**
 * HubCustomSelect
 *
 * React port of the static-v2 `.hub-custom-select` dropdown: a custom-
 * rendered listbox styled to match the Hub360 design system, with a
 * hidden native <select> mirroring the value for a11y/form fallback.
 *
 * Matches the static DOM shape so the CSS in
 * `frontend/src/styles/designerComponents.css` applies 1:1:
 *
 *   <div class="hub-select-wrap agents-environment-select-wrap
 *              hub-custom-select [is-open]">
 *     <button class="hub-custom-trigger" aria-haspopup=listbox
 *             aria-expanded>
 *       <span class="hub-select-icon ..."> <Icon/> </span>
 *       <span class="hub-custom-label">Label</span>
 *       <span class="material-symbols-rounded hub-select-chevron">
 *         expand_more
 *       </span>
 *     </button>
 *     <select class="hub-select hub-select-native" aria-hidden tabIndex=-1>
 *       ...options
 *     </select>
 *     <ul class="hub-custom-menu" role="listbox" [hidden]>
 *       <li><button role=option aria-selected>...</button></li>
 *     </ul>
 *   </div>
 *
 * Props:
 *   id             — used for aria-labelledby / native-select id
 *   value          — current value (string; '' for all)
 *   onChange(v)    — invoked on selection
 *   options        — [{ value, label, disabled? }]
 *   ariaLabel      — for the <select> a11y label
 *   placeholder    — label when no option matches value (e.g. "Todos…")
 *   icon           — optional ReactNode rendered in the leading slot
 *   materialIcon   — Material Symbols name (e.g. "verified_user"); preferred in modals
 *   className      — extra classes on the wrap
 *   disabled       — disables the whole control
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

const MAX_MENU_HEIGHT = 260;
const VIEWPORT_PADDING = 12;

function resetMenuPosition(menu) {
  if (!menu) return;
  menu.style.position = '';
  menu.style.left = '';
  menu.style.right = '';
  menu.style.top = '';
  menu.style.bottom = '';
  menu.style.width = '';
  menu.style.maxHeight = '';
  menu.style.zIndex = '';
}

/** Mirrors prototype `hubPositionCustomMenu` for selects inside modals. */
function positionMenuInModal(wrap, menu) {
  resetMenuPosition(menu);
  wrap.classList.remove('open-up');

  const wrapRect = wrap.getBoundingClientRect();
  const estimatedMenuHeight = Math.min(menu.scrollHeight || MAX_MENU_HEIGHT, MAX_MENU_HEIGHT);
  const compact = wrap.classList.contains('users-per-page-select__control');
  const menuWidth = compact ? wrapRect.width : Math.max(wrapRect.width, 200);
  const left = Math.max(
    VIEWPORT_PADDING,
    Math.min(wrapRect.left, window.innerWidth - menuWidth - VIEWPORT_PADDING),
  );
  const spaceBelow = window.innerHeight - wrapRect.bottom - VIEWPORT_PADDING;
  const spaceAbove = wrapRect.top - VIEWPORT_PADDING;
  const openUp = spaceBelow < Math.max(estimatedMenuHeight, 180) && spaceAbove > spaceBelow;
  const availableSpace = Math.max(120, (openUp ? spaceAbove : spaceBelow) - 6);
  const menuHeight = Math.min(estimatedMenuHeight, availableSpace, MAX_MENU_HEIGHT);
  const top = openUp
    ? Math.max(VIEWPORT_PADDING, wrapRect.top - menuHeight - 6)
    : Math.min(window.innerHeight - VIEWPORT_PADDING - menuHeight, wrapRect.bottom + 6);

  if (openUp) wrap.classList.add('open-up');
  menu.style.position = 'fixed';
  menu.style.left = `${Math.round(left)}px`;
  menu.style.right = 'auto';
  menu.style.top = `${Math.round(top)}px`;
  menu.style.bottom = 'auto';
  menu.style.width = `${Math.round(menuWidth)}px`;
  menu.style.maxHeight = `${Math.round(menuHeight)}px`;
  menu.style.zIndex = '10000';
}

/** Non-modal: fixed menu when viewport space is tight (e.g. footer pagination). */
function positionMenuInline(wrap, menu) {
  const wrapRect = wrap.getBoundingClientRect();
  const estimatedMenuHeight = Math.min(menu.scrollHeight || MAX_MENU_HEIGHT, MAX_MENU_HEIGHT);
  const spaceBelow = window.innerHeight - wrapRect.bottom - VIEWPORT_PADDING;
  const spaceAbove = wrapRect.top - VIEWPORT_PADDING;
  const needsFixed =
    spaceBelow < Math.max(estimatedMenuHeight, 120) && spaceAbove > spaceBelow;

  if (needsFixed || wrap.closest('.users-table-footer')) {
    positionMenuInModal(wrap, menu);
    return;
  }

  resetMenuPosition(menu);
  wrap.classList.remove('open-up');

  const boundsRect = wrap.closest('.modal-dialog')?.getBoundingClientRect() || {
    top: 0,
    bottom: window.innerHeight,
  };
  const requiredSpace = Math.max(estimatedMenuHeight, 180);
  const spaceBelowBounds = boundsRect.bottom - wrapRect.bottom - 20;
  const spaceAboveBounds = wrapRect.top - boundsRect.top - 20;
  if (spaceBelowBounds < requiredSpace && spaceAboveBounds > spaceBelowBounds) {
    wrap.classList.add('open-up');
  }
}

export default function HubCustomSelect({
  id,
  value,
  onChange,
  options = [],
  ariaLabel,
  placeholder = '',
  icon = null,
  materialIcon = '',
  className = '',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((opt) => opt.value === value);
  const displayLabel = selected ? selected.label : placeholder;

  // Position menu (fixed in modals so overflow:hidden on dialog does not clip).
  useEffect(() => {
    if (!open) return undefined;

    const wrap = wrapRef.current;
    const menu = menuRef.current;
    if (!wrap || !menu) return undefined;

    const dialog = wrap.closest('.modal-dialog');

    const apply = () => {
      if (dialog) {
        positionMenuInModal(wrap, menu);
      } else {
        positionMenuInline(wrap, menu);
      }
    };

    const rafId = requestAnimationFrame(apply);
    if (dialog) dialog.classList.add('has-open-select');

    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, true);

    return () => {
      cancelAnimationFrame(rafId);
      resetMenuPosition(menu);
      wrap.classList.remove('open-up');
      if (dialog) dialog.classList.remove('has-open-select');
      window.removeEventListener('resize', apply);
      window.removeEventListener('scroll', apply, true);
    };
  }, [open, options]);

  // Click outside closes the menu. Attached on window so it catches
  // clicks anywhere in the app, including other portaled overlays.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  // Esc closes and returns focus to the trigger. Arrow keys cycle
  // through options when the menu is open.
  const onKeyDown = useCallback(
    (event) => {
      if (!open) {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setOpen(true);
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const items = Array.from(menuRef.current?.querySelectorAll('button[role=option]') || []);
        if (!items.length) return;
        const activeEl = document.activeElement;
        const idx = items.findIndex((el) => el === activeEl);
        const next = event.key === 'ArrowDown'
          ? (idx < 0 ? 0 : Math.min(items.length - 1, idx + 1))
          : (idx <= 0 ? items.length - 1 : idx - 1);
        items[next]?.focus();
      }
    },
    [open],
  );

  const handleSelect = (next) => {
    setOpen(false);
    triggerRef.current?.focus();
    if (next !== value) onChange?.(next);
  };

  // Mirror the value into a hidden native <select> for a11y/form
  // fallback. React will warn if we attach an onChange to a controlled
  // select without an effect, so we use a noop handler.
  const onNativeChange = (event) => {
    onChange?.(event.target.value);
  };

  return (
    <div
      ref={wrapRef}
      className={`hub-select-wrap hub-custom-select${open ? ' is-open' : ''}${
        className ? ` ${className}` : ''
      }`}
      onKeyDown={onKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className="hub-custom-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id ? `${id}-menu` : undefined}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        {materialIcon ? (
          <span className="material-symbols-rounded hub-select-icon" aria-hidden>
            {materialIcon}
          </span>
        ) : null}
        {!materialIcon && icon ? (
          <span className="hub-select-icon hub-select-icon--lucide" aria-hidden>
            {icon}
          </span>
        ) : null}
        <span
          className={`hub-custom-label${
            !selected && placeholder ? ' is-placeholder' : ''
          }`}
        >
          {displayLabel}
        </span>
        <span className="material-symbols-rounded hub-select-chevron" aria-hidden>
          expand_more
        </span>
      </button>
      <select
        id={id}
        className="hub-select hub-select-native"
        aria-label={ariaLabel}
        value={value ?? ''}
        onChange={onNativeChange}
        tabIndex={-1}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      <ul
        ref={menuRef}
        className="hub-custom-menu"
        role="listbox"
        id={id ? `${id}-menu` : undefined}
        hidden={!open}
      >
        {options.map((opt) => {
          const isSel = opt.value === value;
          return (
            <li key={opt.value} role="none">
              <button
                type="button"
                role="option"
                aria-selected={isSel}
                className={isSel ? 'is-selected' : ''}
                disabled={opt.disabled}
                onClick={() => handleSelect(opt.value)}
                // Allow arrow-key flow via focus management in onKeyDown.
                tabIndex={open ? 0 : -1}
              >
                {opt.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
